import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkLoadError: boolean;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkLoadError: false };
  }

  private isDarkMode(): boolean {
    const storedTheme = localStorage.getItem('themeMode');
    if (storedTheme === 'dark') return true;
    if (storedTheme === 'light') return false;
    // For 'system' or no preference, check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  static getDerivedStateFromError(error: Error) {
    // Check if it's a chunk load error
    const errorMessage = error.message?.toLowerCase() || '';
    const isChunkLoadError =
      errorMessage.includes('failed to fetch dynamically imported module') ||
      errorMessage.includes('importing a module script failed') ||
      errorMessage.includes('loading chunk') ||
      errorMessage.includes('failed to import') ||
      errorMessage.includes('unable to preload css') ||
      errorMessage.includes('dynamically imported module');

    return { hasError: true, error, isChunkLoadError };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error);
    console.error('Error info:', errorInfo);

    // Check if it's a chunk load error
    const errorMessage = error.message?.toLowerCase() || '';
    const isChunkLoadError =
      errorMessage.includes('failed to fetch dynamically imported module') ||
      errorMessage.includes('importing a module script failed') ||
      errorMessage.includes('loading chunk') ||
      errorMessage.includes('failed to import') ||
      errorMessage.includes('unable to preload css') ||
      errorMessage.includes('dynamically imported module');

    if (isChunkLoadError) {
      console.warn('Chunk load failure detected - likely due to new deployment');

      // Auto-reload once to pick up fresh assets (transparent to user)
      const reloadKey = 'chunk-error-auto-reload';
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();

      // Only auto-reload if we haven't done so in the last 30 seconds
      if (!lastReload || now - parseInt(lastReload) > 30000) {
        console.log('Auto-reloading to fetch latest version...');
        sessionStorage.setItem(reloadKey, now.toString());
        window.location.reload();
        return;
      }

      console.log('Auto-reload already attempted, showing manual refresh UI');
    }
  }

  private shouldAutoReload(): boolean {
    if (!this.state.isChunkLoadError) return false;
    const lastReload = sessionStorage.getItem('chunk-error-auto-reload');
    if (!lastReload) return true;
    return Date.now() - parseInt(lastReload) > 30000;
  }

  render() {
    if (this.state.hasError) {
      const isDark = this.isDarkMode();
      const bg = isDark ? '#121212' : '#f9fafb';

      // Set body/html background to match so there's no white border
      document.documentElement.style.background = bg;
      document.body.style.background = bg;

      // If auto-reload is about to fire, render nothing to avoid a flash
      if (this.shouldAutoReload()) {
        return null;
      }

      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: bg,
            color: isDark ? '#F8F8F8' : '#222',
            padding: 32,
            fontFamily: 'Inter, Roboto, Arial, sans-serif',
          }}
        >
            <img
              src={isDark ? '/assets/images/logo-white-blue.png' : '/assets/images/logo-standard.png'}
              alt="Moddy AI"
              style={{
                height: '2rem',
              }}
            />
          <h1 style={{ marginBottom: 16 }}>
            {this.state.isChunkLoadError
              ? 'New Version Available'
              : 'Oops! Something went wrong.'}
          </h1>
          <p style={{ maxWidth: 400, textAlign: 'center', marginBottom: 16 }}>
            {this.state.isChunkLoadError ? (
              <>
                A new version of Moddy has been deployed.<br />
                Please refresh the page to load the latest version and continue.
              </>
            ) : (
              <>
                This can sometimes happen after we update the site.<br />
                Please try refreshing the page to get the latest version.<br /><br />
                If the problem persists, contact support.
              </>
            )}
          </p>
          <button
            onClick={async () => {
              if (this.state.isChunkLoadError) {
                // Clear any service worker / cache storage
                if ('caches' in window) {
                  try {
                    const names = await caches.keys();
                    await Promise.all(names.map(name => caches.delete(name)));
                  } catch { /* ignore */ }
                }
                // Reset auto-reload guard so next load gets another automatic attempt
                sessionStorage.removeItem('chunk-error-auto-reload');
                // Navigate with cache-busting param to bypass CDN and browser cache
                const url = new URL(window.location.href);
                url.searchParams.set('_r', Date.now().toString());
                window.location.href = url.toString();
              } else {
                window.location.reload();
              }
            }}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              marginBottom: '16px',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#2563eb';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#3b82f6';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
              <path d="M21 3v5h-5"/>
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
              <path d="M3 21v-5h5"/>
            </svg>
            {this.state.isChunkLoadError ? 'Load New Version' : 'Refresh Page'}
          </button>
          {!this.state.isChunkLoadError && (
            <pre
              style={{
                background: isDark ? '#2a1a1a' : '#fff0f0',
                color: isDark ? '#ff8a80' : '#b71c1c',
                padding: 12,
                borderRadius: 6,
                maxWidth: 500,
                overflowX: 'auto',
                fontSize: 14,
              }}
            >
              {this.state.error?.toString()}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 